import { useEffect, useMemo, useState } from "react";
import {
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { MainLayout } from "@shared/ui/MainLayout";
import { InputController, SelectController, InputDatePickerController } from "@shared/ui/form";
import { ErrorServer, InfoAlert, SuccessAlert, ErrorAlert } from "@shared/ui/alerts";
import MDButton from "@vendor/components/MDButton";
import MDBox from "@vendor/components/MDBox";
import UploadFile from "@vendor/examples/UploadFile";

import type { TranslationKey } from "@shared/i18n/translationKey";
import type { RegisterRequestValues } from "../api/licenseApplicationApi";

import { buildRegisterRequestSchema } from "../schema/registerRequestSchema";
import { buildLicenseApplicationFormData } from "../api/licenseApplicationApi";
import { useLicenseApplications } from "../hooks/useLicenseApplications";
import { useVacationDaysAvailable } from "../hooks/useVacationDaysAvailable";
import { usePickupFieldsMetadata } from "../hooks/usePickupFieldsMetadata";
import { useSupervisors } from "../hooks/useSupervisors";
import { useRegisterLicenseApplication } from "../hooks/useRegisterLicenseApplication";
import { DEFAULT_VACATION_DAYS, LEAVE_TYPE, TYPES_FROM_AVAILABILITY } from "../constants";
import {
  businessDaysBetween,
  clampDays,
  evaluateRequestedDays,
  findActivePendingRequest,
  isAllowedFileType,
  isWithinFileSize,
  proofIllnessMinDate,
} from "../lib/licenseApplicationRules";

const validationFileInitial = { required: false, type: false, size: false };

export const RegisterRequestPage = () => {
  const { t } = useTranslation();
  const schema = useMemo(() => buildRegisterRequestSchema(t), [t]);
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const [file, setFile] = useState<File | null>(null);
  const [validateFile, setValidateFile] = useState(validationFileInitial);
  const [isDaysRequest, setIsDaysRequest] = useState(false);
  const navigate = useNavigate();

  const { data: requestList, isError: isListError } = useLicenseApplications();
  const { data: dayAvalibity = DEFAULT_VACATION_DAYS, isLoading: isLoadingDays } =
    useVacationDaysAvailable();
  const { data: metaField = [], isLoading: isLoadingMeta, isError: isMetaError } =
    usePickupFieldsMetadata();
  const {
    data: emailSupervisorList = [],
    isLoading: isLoadingSupervisors,
    isError: isSupervisorsError,
  } = useSupervisors();
  const registerMutation = useRegisterLicenseApplication();

  const loading =
    isLoadingDays || isLoadingMeta || isLoadingSupervisors || registerMutation.isPending;

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const type = watch("type");

  useEffect(() => {
    if (isMetaError || isSupervisorsError || isListError) ErrorServer().then();
  }, [isMetaError, isSupervisorsError, isListError]);

  useEffect(() => {
    if (type === LEAVE_TYPE.OTHER) setFile(null);
    validateType();
  }, [endDate, startDate, type]);

  const validateRequestPending = () => {
    const pending = findActivePendingRequest(requestList ?? [], type);
    if (pending) {
      InfoAlert(t("invalidRequest"), t("alreadyRequest", { type, status: pending.status })).then();
    }
  };

  const validateType = () => {
    const days = businessDaysBetween(startDate, endDate);
    if (TYPES_FROM_AVAILABILITY.includes(type)) validateRequestPending();
    const result = evaluateRequestedDays({ type, days, vacationDays: dayAvalibity });
    if (!result.valid) {
      setIsDaysRequest(true);
      // Con `valid: false` la regla siempre completa ambas claves; el fallback vacio se conserva
      // tal cual y solo se tipa como clave para que compile.
      const emptyKey = "" as TranslationKey;
      InfoAlert(t(result.titleKey ?? emptyKey), t(result.messageKey ?? emptyKey, result.params)).then();
      return false;
    }
    setIsDaysRequest(false);
    return true;
  };

  const requiredFile = () => {
    setValidateFile({ type: false, size: false, required: true });
  };

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) {
      requiredFile();
      return;
    }
    // El guard de arriba garantiza al menos un archivo.
    const selected = e.target.files[0]!;
    if (!isAllowedFileType(selected.name)) {
      setValidateFile({ required: true, type: true, size: false });
      return;
    }
    if (!isWithinFileSize(selected.size)) {
      setValidateFile({ required: false, type: false, size: true });
      return;
    }
    setValidateFile(validationFileInitial);
    setFile(selected);
  };

  const onRegister = (value: RegisterRequestValues) => {
    if (!validateType()) return;
    if (isDaysRequest) return;
    if (file !== null && !isWithinFileSize(file.size)) {
      InfoAlert(t("maximumFileSizeInvoice")).then();
      return;
    }
    if (file == null && type !== LEAVE_TYPE.OTHER) {
      requiredFile();
      return;
    }
    const formData = buildLicenseApplicationFormData(file, value);
    registerMutation.mutate(formData, {
      onSuccess: () => {
        SuccessAlert(t("applicationRegistration"), t("doneSuccessfully", { a: "o" })).then(() =>
          navigate("/solicitudes")
        );
      },
      onError: (error) => {
        const data = error.response?.data;
        ErrorAlert(t("errorTitle"), data?.message ?? t("internalError")).then();
      },
    });
  };

  const minSelectableDate = () => proofIllnessMinDate(type);

  return (
    <MainLayout loading={loading}>
      <Card>
        <MDBox p={3}>
          <Typography variant="h5">{t("registerApplication")}</Typography>
          <form onSubmit={handleSubmit(onRegister)}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <SelectController
                    control={control}
                    defaultValue={[]}
                    label={t("type")}
                    name="type"
                    listValue={metaField}
                  />
                  {errors.type && (
                    <span style={{ fontSize: 12, color: "red" }}>{errors.type.message}</span>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <Controller
                    name="supervisorEmail"
                    control={control}
                    defaultValue=""
                    rules={{ required: true, maxLength: 50 }}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">
                          {t("supervisorEmail")}
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label="Age"
                          {...field}
                        >
                          {emailSupervisorList.map((emailSupervisor, index) => (
                            <MenuItem key={index} value={emailSupervisor.email}>
                              {emailSupervisor.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  {errors.supervisorEmail && (
                    <span style={{ fontSize: 12, color: "red" }}>
                      {errors.supervisorEmail.message}
                    </span>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputDatePickerController
                    control={control}
                    disabledWeekEnds
                    defaultValue={minSelectableDate()}
                    minDate={minSelectableDate()}
                    openTo="month"
                    name="startDate"
                    label={t("startDate")}
                    views={["year", "month", "day"]}
                    readOnlyInput={true}
                  />
                  {errors.startDate && (
                    <span style={{ fontSize: 12, color: "red" }}>{errors.startDate.message}</span>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputDatePickerController
                    control={control}
                    disabledWeekEnds
                    defaultValue={minSelectableDate()}
                    minDate={minSelectableDate()}
                    openTo="month"
                    name="endDate"
                    label={t("endDate")}
                    views={["year", "month", "day"]}
                    readOnlyInput={true}
                  />
                  {errors.endDate && (
                    <span style={{ fontSize: 12, color: "red" }}>{errors.endDate.message}</span>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputController
                    name="comments"
                    control={control}
                    type="text"
                    defaultValue=""
                    label={t("observations")}
                  />
                  {errors.comments && (
                    <span style={{ fontSize: 12, color: "red" }}>{errors.comments.message}</span>
                  )}
                </FormControl>
              </Grid>

              {type !== LEAVE_TYPE.OTHER && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <UploadFile
                      id="coverPage"
                      nameInput="cover"
                      onChangeFile={handleChangeFile}
                      file={file}
                      textButton={t("uploadEvidence")}
                      fileLoaded={t("uploadedEvidence")}
                    />
                    {Boolean(validateFile.size) && (
                      <span style={{ fontSize: 12, color: "red" }}>
                        {t("maximumFileSizeInvoice")}
                      </span>
                    )}
                    {validateFile.type && (
                      <span style={{ fontSize: 12, color: "red" }}>
                        {t("applicationFileValid")}
                      </span>
                    )}
                    {validateFile.required && (
                      <span style={{ fontSize: 12, color: "red" }}>{t("attachApproval")}</span>
                    )}
                  </FormControl>
                </Grid>
              )}

              <Grid size={12}>
                <Typography variant="h6">
                  {t("daysAvailability")}: {clampDays(dayAvalibity.daysAvailability ?? 0)}
                </Typography>
                <Typography variant="h6">
                  {t("daysSicknessRecord")}: {clampDays(dayAvalibity.daysSicknessRecord ?? 0)}
                </Typography>
                <Typography variant="h6">
                  {t("daysAvailabilityPaid")}: {clampDays(dayAvalibity.daysAvailabilityPaid ?? 0)}
                </Typography>
              </Grid>

              <Grid size={12}>
                <MDButton variant="gradient" type="submit" color="info" fullWidth>
                  {t("register")}
                </MDButton>
              </Grid>
            </Grid>
          </form>
        </MDBox>
      </Card>
    </MainLayout>
  );
};
